import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Chip } from "@/components/Chip";
import { FaceScanner } from "@/components/FaceScanner";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Plus, Pencil, Trash2, ScanFace, X, FileCheck2, Upload, Download, AlertTriangle, KeyRound } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { k: "name", label: "Full Name", type: "text" },
  { k: "mobile", label: "Mobile (login)", type: "tel" },
  { k: "email", label: "Email", type: "email" },
  { k: "phone", label: "Phone", type: "text" },
  { k: "department", label: "Department", type: "text" },
  { k: "designation", label: "Designation", type: "text" },
  { k: "join_date", label: "Join Date", type: "date" },
  { k: "aadhaar_number", label: "Aadhaar Number (12 digits)", type: "text" },
];

const empty = { name: "", mobile: "", email: "", phone: "", department: "", designation: "", join_date: "", aadhaar_number: "", role: "employee" };

const DOC_SLOTS = [
  { key: "aadhaar_card_front", label: "Aadhaar (Front)", required: true },
  { key: "aadhaar_card_back",  label: "Aadhaar (Back)",  required: false },
  { key: "policies_signed",    label: "Signed Policies", required: true },
  { key: "bio_data",           label: "Bio-Data (Signed)", required: true },
];

export default function Team() {
  const { user, refresh: refreshAuth } = useAuth();
  const { settings } = useSettings();
  const [users, setUsers] = useState([]);
  const [faceIssues, setFaceIssues] = useState([]);
  const [editing, setEditing] = useState(null); // user object or "new"
  const [form, setForm] = useState(empty);
  const [scanFor, setScanFor] = useState(null);
  const [resetFor, setResetFor] = useState(null); // user object whose PIN we're resetting

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); } catch { /* noop */ }
    try { const { data } = await api.get("/faces/quality-report"); setFaceIssues(data || []); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (u) => {
    setForm({ name: u.name, mobile: u.mobile || "", email: u.email || "", phone: u.phone || "", department: u.department || "", designation: u.designation || "", join_date: u.join_date || "", role: u.role });
    setEditing(u);
  };

  const submit = async () => {
    try {
      if (editing === "new") {
        await api.post("/users", form);
        toast.success("Employee added — they can now login & set their PIN.");
      } else {
        await api.patch(`/users/${editing.user_id}`, form);
        toast.success("Updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const onFaceDone = async ({ descriptors, simulated }) => {
    if (simulated || !descriptors) { setScanFor(null); toast.message("Simulation captured"); return; }
    try {
      await api.post(`/users/${scanFor.user_id}/face`, { descriptors });
      toast.success(`Face saved (${descriptors.length} samples)`);
      if (scanFor.user_id === user.user_id) await refreshAuth();
      setScanFor(null);
      load();
    } catch { toast.error("Could not save"); }
  };

  const isAdmin = user?.role === "admin";
  const isHR = user?.role === "admin" || user?.role === "hr_manager";  // admin or HR Manager
  const hrCanEditEmployees = !!settings?.hr_can_edit_employees;
  const canAdd = isHR;
  const canEdit = isAdmin || (user?.role === "hr_manager" && hrCanEditEmployees);
  const canDelete = isAdmin || (user?.role === "hr_manager" && hrCanEditEmployees);

  return (
    <div className="px-4 pt-3 pb-4 space-y-3" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Workforce</div>
          <div className="font-syne text-2xl font-bold tracking-tight" style={{ color: "#E8ECFF" }}>Team ({users.length})</div>
        </div>
        {canAdd && (
          <button data-testid="add-employee-button" onClick={openNew} className="h-10 px-4 rounded-xl flex items-center gap-1.5 font-bold text-sm"
            style={{ background: "#4F8EF7", color: "#fff" }}>
            <Plus size={16} /> Add
          </button>
        )}
      </div>

      {isHR && faceIssues.length > 0 && (
        <div className="rounded-2xl p-3 flex items-start gap-3" style={{ background: "rgba(248,113,113,0.08)", border: "1px solid #f87171" }} data-testid="face-issues-banner">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" style={{ color: "#f87171" }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" style={{ color: "#E8ECFF" }}>
              {faceIssues.length} employee{faceIssues.length > 1 ? "s" : ""} need face re-registration
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: "#fca5a5" }}>
              Legacy or ambiguous enrollments can confuse kiosk recognition — tap &ldquo;Face&rdquo; on their card to re-register.
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.user_id} className="rounded-2xl p-4" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid={`emp-card-${u.user_id}`}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold font-syne" style={{ background: "#1E2640", color: "#E8ECFF" }}>
                {u.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate" style={{ color: "#E8ECFF" }}>{u.name}</div>
                <div className="text-xs truncate" style={{ color: "#7A85A3" }}>{u.designation || "—"}{u.department ? ` · ${u.department}` : ""}</div>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <Chip color={u.role === "admin" ? "purple" : u.role === "hr_manager" ? "blue" : "gray"}>
                    {u.role === "admin" ? "Admin" : u.role === "hr_manager" ? "HR Manager" : "Employee"}
                  </Chip>
                  <Chip color={u.has_pin ? "green" : "amber"}>{u.has_pin ? "PIN set" : "PIN pending"}</Chip>
                  <Chip color={u.face_descriptor ? "green" : "red"}>{u.face_descriptor ? "Face ✓" : "No Face"}</Chip>
                  {faceIssues.find((f) => f.user_id === u.user_id) && (
                    <Chip color="red" data-testid={`face-issue-${u.user_id}`}>Face ⚠</Chip>
                  )}
                  <Chip color={u.docs_status?.complete ? "green" : "red"} data-testid={`docs-${u.user_id}`}>
                    {u.docs_status?.complete ? "Docs ✓" : "Docs pending"}
                  </Chip>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              {(isHR || u.user_id === user.user_id) && (
                <button onClick={() => setScanFor(u)} data-testid={`register-face-${u.user_id}`} className="flex-1 h-10 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5"
                  style={{ background: "transparent", color: "#4F8EF7", border: "1px solid rgba(79,142,247,0.4)" }}>
                  <ScanFace size={14} /> {u.face_descriptor ? "Re-register" : "Register Face"}
                </button>
              )}
              {canEdit && (
                <button onClick={() => openEdit(u)} data-testid={`edit-${u.user_id}`} className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#1E2640", color: "#E8ECFF" }}>
                  <Pencil size={14} />
                </button>
              )}
              {isHR && u.user_id !== user.user_id && u.role !== "admin" && (
                <button
                  onClick={() => setResetFor(u)}
                  data-testid={`reset-pin-${u.user_id}`}
                  title="Reset PIN"
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: "rgba(251,191,36,0.1)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.3)" }}
                >
                  <KeyRound size={14} />
                </button>
              )}
              {canDelete && u.user_id !== user.user_id && (
                <button onClick={() => remove(u)} data-testid={`delete-${u.user_id}`} className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(247,90,90,0.1)", color: "#F75A5A", border: "1px solid rgba(247,90,90,0.3)" }}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Drawer open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DrawerContent style={{ background: "#111520", borderColor: "#1E2640" }}>
          <div className="mx-auto w-12 h-1.5 rounded-full mt-2 mb-3" style={{ background: "#1E2640" }} />
          <div className="px-6 pb-8 max-h-[80vh] overflow-y-auto" data-testid="employee-form">
            <div className="flex items-center justify-between mb-4">
              <div className="font-syne text-xl font-bold tracking-tight" style={{ color: "#E8ECFF" }}>
                {editing === "new" ? "Add Employee" : "Edit Employee"}
              </div>
              <button onClick={() => setEditing(null)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#7A85A3" }}>
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              {FIELDS.map((f) => (
                <div key={f.k}>
                  <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#7A85A3" }}>{f.label}</div>
                  <input type={f.type} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`form-${f.k}`}
                    className="w-full h-11 px-3 rounded-xl" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
                </div>
              ))}
              {isAdmin && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#7A85A3" }}>Role</div>
                  <select value={form.role === "admin" ? "admin" : form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="form-role"
                    disabled={form.role === "admin"}
                    className="w-full h-11 px-3 rounded-xl disabled:opacity-60" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }}>
                    {form.role === "admin" && <option value="admin">Admin (cannot be changed)</option>}
                    <option value="hr_manager">HR Manager</option>
                    <option value="employee">Employee</option>
                  </select>
                </div>
              )}
              <button onClick={submit} data-testid="form-save" className="w-full h-12 rounded-xl font-bold" style={{ background: "#4F8EF7", color: "#fff" }}>
                Save
              </button>
              {editing !== "new" && editing?.user_id && (isHR || editing.user_id === user?.user_id) && (
                <DocumentsSection userId={editing.user_id} canManage={isHR} />
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <FaceScanner open={!!scanFor} mode="register" title={`Register: ${scanFor?.name || ""}`} onDone={onFaceDone} onClose={() => setScanFor(null)} />
      <ResetPinDialog user={resetFor} onClose={() => setResetFor(null)} onDone={() => { setResetFor(null); load(); }} />
    </div>
  );
}

function ResetPinDialog({ user, onClose, onDone }) {
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNewPin(""); setConfirmPin(""); }, [user?.user_id]);
  if (!user) return null;

  const digitsOnly = (v) => v.replace(/\D/g, "").slice(0, 6);
  const validLen = newPin.length >= 4 && newPin.length <= 6;
  const match = newPin && newPin === confirmPin;
  const canSave = validLen && match && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.post(`/users/${user.user_id}/reset-pin`, { new_pin: newPin });
      toast.success(`PIN updated for ${user.name}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset PIN");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(5,7,15,0.7)" }}
      data-testid="reset-pin-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl p-5 space-y-4"
        style={{ background: "#111520", border: "1px solid #1E2640" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "#fbbf24" }}>Reset PIN</div>
            <div className="font-syne text-lg font-bold" style={{ color: "#E8ECFF" }}>{user.name}</div>
            <div className="text-xs" style={{ color: "#7A85A3" }}>{user.mobile}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="reset-pin-close"
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#7A85A3" }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="rounded-xl p-3 text-[11px]" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)", color: "#fde68a" }}>
          You are setting a new PIN for this user. Their existing PIN will stop working immediately. Share the new PIN with them securely.
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#7A85A3" }}>New PIN (4–6 digits)</div>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={newPin}
            onChange={(e) => setNewPin(digitsOnly(e.target.value))}
            data-testid="reset-pin-new"
            className="w-full h-11 px-3 rounded-xl tracking-widest"
            style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }}
          />
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#7A85A3" }}>Confirm New PIN</div>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={confirmPin}
            onChange={(e) => setConfirmPin(digitsOnly(e.target.value))}
            data-testid="reset-pin-confirm"
            className="w-full h-11 px-3 rounded-xl tracking-widest"
            style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }}
          />
          {confirmPin && !match && (
            <div className="text-[11px] mt-1" style={{ color: "#F75A5A" }}>PINs do not match</div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            data-testid="reset-pin-cancel"
            className="flex-1 h-11 rounded-xl font-bold text-sm"
            style={{ background: "#1E2640", color: "#E8ECFF" }}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSave}
            data-testid="reset-pin-save"
            className="flex-1 h-11 rounded-xl font-bold text-sm disabled:opacity-50"
            style={{ background: "#4F8EF7", color: "#fff" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocumentsSection({ userId, canManage }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null); // doc_type key being uploaded
  const load = React.useCallback(async () => {
    try { const { data } = await api.get(`/documents/user/${userId}`); setDocs(data || []); } catch { setDocs([]); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const byType = React.useMemo(() => {
    const m = {};
    docs.forEach((d) => { m[d.doc_type] = d; });
    return m;
  }, [docs]);

  const onFile = async (docType, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/(pdf|png|jpe?g|webp)$/i.test(file.name)) { toast.error("Only PDF/JPG/PNG allowed"); return; }
    if (file.size < 10 * 1024) { toast.error("File too small — need a readable scan (>10 KB)"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large — max 8 MB"); return; }
    setUploading(docType);
    const fd = new FormData();
    fd.append("user_id", userId);
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Delete ${doc.original_filename}?`)) return;
    try { await api.delete(`/documents/${doc.id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.original_filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    }
  };

  return (
    <div className="rounded-2xl p-3 space-y-2 mt-4" style={{ background: "#0A0D14", border: "1px solid #1E2640" }} data-testid={`docs-section-${userId}`}>
      <div className="flex items-center gap-2">
        <FileCheck2 size={14} style={{ color: "#22D3A5" }} />
        <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Compliance Documents</div>
      </div>
      {DOC_SLOTS.map((slot) => {
        const doc = byType[slot.key];
        return (
          <div key={slot.key} className="flex items-center gap-2 rounded-xl p-2" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold flex items-center gap-1" style={{ color: "#E8ECFF" }}>
                {slot.label}
                {slot.required && <span style={{ color: "#F75A5A" }}>*</span>}
                {!doc && slot.required && <AlertTriangle size={11} style={{ color: "#fb923c" }} />}
              </div>
              <div className="text-[10px] truncate" style={{ color: "#7A85A3" }}>
                {doc ? `${doc.original_filename} · ${(doc.size / 1024).toFixed(0)} KB · by ${doc.uploaded_by_name || "—"}` : "Not uploaded"}
              </div>
            </div>
            <div className="flex gap-1">
              {doc && (
                <button onClick={() => download(doc)} data-testid={`dl-${slot.key}`} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#0f1a2a", color: "#4F8EF7" }}>
                  <Download size={13} />
                </button>
              )}
              {canManage && (
                <label className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer" style={{ background: "#0f2a1f", color: "#22D3A5" }}>
                  {uploading === slot.key ? <span className="text-[10px]">…</span> : <Upload size={13} />}
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*" onChange={(e) => onFile(slot.key, e)} className="hidden" data-testid={`upload-${slot.key}`} />
                </label>
              )}
              {canManage && doc && (
                <button onClick={() => onDelete(doc)} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#2a0f0f", color: "#F75A5A" }}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
        );
      })}
      <div className="text-[10px]" style={{ color: "#7A85A3" }}>
        PDF/JPG/PNG · min 10 KB · max 8 MB. Uploads are verified automatically since only HR can upload.
      </div>
    </div>
  );
}
